import * as React from 'react';
import { overlayAnimation } from '@wayste/shadcn/lib/animations';
import { cn } from '@wayste/shadcn/lib/utils';
import * as DrawerPrimitive from '@radix-ui/react-dialog';
import { type VariantProps, cva } from 'class-variance-authority';
import { XIcon } from 'lucide-react';
import { ActionFooter, type ActionFooterAction } from './action-footer';
import { Button } from './button';
import { Toolbar, ToolbarTitle } from './toolbar';

/**
 * Drawer Component
 *
 * A sliding panel component that appears from the edge of the screen.
 * Built on top of Radix UI Dialog primitive.
 *
 * @example
 * ```tsx
 * import { Drawer, DrawerTrigger, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter, DrawerClose } from '@wayste/shadcn';
 *
 * function MyComponent() {
 *   return (
 *     <Drawer>
 *       <DrawerTrigger asChild>
 *         <button>Open Drawer</button>
 *       </DrawerTrigger>
 *       <DrawerContent side="right">
 *         <DrawerHeader>
 *           <DrawerTitle>Drawer Title</DrawerTitle>
 *           <DrawerDescription>This is a description of the drawer content.</DrawerDescription>
 *         </DrawerHeader>
 *         <div className="py-4">
 *           Your drawer content goes here
 *         </div>
 *         <DrawerFooter>
 *           <DrawerClose asChild>
 *             <button>Cancel</button>
 *           </DrawerClose>
 *           <button>Submit</button>
 *         </DrawerFooter>
 *       </DrawerContent>
 *     </Drawer>
 *   );
 * }
 * ```
 */

/**
 * Root drawer component that manages the open/closed state.
 * Use the `open` and `onOpenChange` props to control the drawer state.
 */
const Drawer = DrawerPrimitive.Root;

/**
 * Trigger element that opens the drawer when clicked.
 * Use `asChild` prop to compose with your own button component.
 * @example <DrawerTrigger asChild><Button>Open</Button></DrawerTrigger>
 */
const DrawerTrigger = DrawerPrimitive.Trigger;

/**
 * Element that closes the drawer when clicked.
 * Use `asChild` prop to compose with your own button component.
 * @example <DrawerClose asChild><Button>Close</Button></DrawerClose>
 */
const DrawerClose = DrawerPrimitive.Close;

/**
 * Portal component that renders the drawer in a React portal.
 * This is automatically used within DrawerContent, no need to use directly.
 */
const DrawerPortal = DrawerPrimitive.Portal;

/**
 * Semi-transparent overlay that appears behind the drawer.
 * This is automatically rendered within DrawerContent, no need to use directly.
 */
const DrawerOverlay = ({ className, ref, ...props }: React.ComponentPropsWithRef<typeof DrawerPrimitive.Overlay>) => (
    <DrawerPrimitive.Overlay ref={ref} className={cn(overlayAnimation, 'fixed inset-0 z-[350] bg-black/50', className)} {...props} />
);
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

export type DrawerWidth = 'sm' | 'md' | 'lg';

const drawerWidthMap: Record<DrawerWidth, string> = {
    sm: 'w-[400px]',
    md: 'w-[600px]',
    lg: 'w-[800px]',
};

const drawerVariants = cva(
    'fixed z-[350] bg-surface-tertiary p-0 shadow-lg ease-out data-[state=closed]:duration-200 data-[state=open]:duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
    {
        variants: {
            side: {
                top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
                bottom: 'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
                left: 'inset-y-0 left-0 h-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
                right: 'inset-y-0 right-0 h-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
            },
        },
        defaultVariants: {
            side: 'right',
        },
    },
);

interface DrawerContentProps extends React.ComponentPropsWithRef<typeof DrawerPrimitive.Content>, VariantProps<typeof drawerVariants> {
    /**
     * Whether to show the close button (X icon) in the top right corner.
     * @default true
     */
    showCloseButton?: boolean;
    /**
     * When true, applies overflow-hidden for use with DrawerBody scrollable content.
     * @default false
     */
    scrollable?: boolean;
    /**
     * Width of the drawer panel. Only applies to left/right drawers.
     * - sm — 400px (default)
     * - md — 600px
     * - lg — 800px
     * @default 'sm'
     */
    width?: DrawerWidth;
    /**
     * When true, suppresses the semi-transparent backdrop so the rest of the page stays
     * visible and interactive. Pair with `<Drawer modal={false}>` for a non-modal panel.
     * @default false
     */
    hideOverlay?: boolean;
}

/**
 * Main content container for the drawer.
 *
 * @param side - Which side of the screen the drawer slides in from: 'top' | 'bottom' | 'left' | 'right'
 * @param width - Width of the drawer: 'sm' (400px) | 'md' (600px) | 'lg' (800px). Only applies to left/right drawers.
 * @param showCloseButton - Whether to display the close (X) button (default: true)
 * @param scrollable - When true, applies overflow-hidden for use with DrawerBody (default: false)
 *
 * @example
 * ```tsx
 * <DrawerContent side="right" width="md" showCloseButton={false}>
 *   <DrawerToolbar label="Title" />
 *   Content here
 *   <DrawerFooter primaryAction={{ label: 'Save', onClick: handleSave }} />
 * </DrawerContent>
 * ```
 */
const DrawerContent = ({
    side = 'right',
    width = 'sm',
    className,
    children,
    showCloseButton = true,
    scrollable = false,
    hideOverlay = false,
    ref,
    ...props
}: DrawerContentProps) => {
    const isHorizontal = side === 'left' || side === 'right';
    const widthClass = isHorizontal ? drawerWidthMap[width] : '';
    const scrollableClass = scrollable ? 'overflow-hidden' : '';

    return (
        <DrawerPortal>
            {!hideOverlay && <DrawerOverlay />}
            <DrawerPrimitive.Content
                ref={ref}
                className={cn(drawerVariants({ side }), 'flex flex-col', widthClass, scrollableClass, className)}
                {...props}
            >
                {children}
                {showCloseButton && (
                    <DrawerPrimitive.Close asChild>
                        <Button variant="ghost" size="icon-sm" className="absolute right-4 top-4">
                            <XIcon />
                            <span className="sr-only">Close</span>
                        </Button>
                    </DrawerPrimitive.Close>
                )}
            </DrawerPrimitive.Content>
        </DrawerPortal>
    );
};
DrawerContent.displayName = DrawerPrimitive.Content.displayName;

/**
 * Container for the drawer's header section.
 * Typically contains DrawerTitle and DrawerDescription.
 * Automatically styled with appropriate spacing and alignment.
 *
 * @example
 * ```tsx
 * <DrawerHeader>
 *   <DrawerTitle>Delete Account</DrawerTitle>
 *   <DrawerDescription>This action cannot be undone.</DrawerDescription>
 * </DrawerHeader>
 * ```
 */
const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('border-border-default flex flex-col gap-2 border-b text-center sm:text-left', className)} {...props} />
);
DrawerHeader.displayName = 'DrawerHeader';

interface DrawerFooterProps {
    /**
     * Primary action — renders on the far right as a filled primary button.
     */
    primaryAction?: ActionFooterAction;
    /**
     * Secondary action — renders to the left of the primary button.
     */
    secondaryAction?: ActionFooterAction;
    /**
     * Tertiary action — renders on the far left as an outlined button.
     */
    tertiaryAction?: ActionFooterAction;
    className?: string;
    children?: React.ReactNode;
}

/**
 * Footer for the drawer, backed by ActionFooter at size="default" to match
 * the DrawerToolbar. Supports primaryAction / secondaryAction / tertiaryAction
 * props, or pass children for fully custom content.
 *
 * To close the drawer on button click, call your `onOpenChange(false)` inside
 * the action's `onClick` handler, or wrap the whole footer in a DrawerClose.
 *
 * @example
 * ```tsx
 * <DrawerFooter
 *   primaryAction={{ label: 'Save', onClick: handleSave, disabled: !isValid }}
 *   secondaryAction={{ label: 'Cancel', onClick: () => setOpen(false) }}
 *   tertiaryAction={{ label: 'Delete', onClick: handleDelete }}
 * />
 * ```
 */
const DrawerFooter = ({ className, children, primaryAction, secondaryAction, tertiaryAction }: DrawerFooterProps) => (
    <ActionFooter
        size="default"
        className={cn('mt-auto', className)}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        tertiaryAction={tertiaryAction}
    >
        {children}
    </ActionFooter>
);
DrawerFooter.displayName = 'DrawerFooter';

/**
 * Title text for the drawer.
 * Should be used within DrawerHeader for proper styling and accessibility.
 * Required for accessibility - every drawer should have a title.
 */
const DrawerTitle = ({ className, ref, ...props }: React.ComponentPropsWithRef<typeof DrawerPrimitive.Title>) => (
    <DrawerPrimitive.Title ref={ref} className={cn('text-text-primary text-lg font-semibold', className)} {...props} />
);
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

/**
 * Descriptive text for the drawer.
 * Should be used within DrawerHeader to provide additional context about the drawer's purpose.
 * Optional but recommended for better accessibility and user experience.
 *
 * @example
 * ```tsx
 * <DrawerDescription>
 *   Please review the details below before proceeding.
 * </DrawerDescription>
 * ```
 */
const DrawerDescription = ({ className, ref, ...props }: React.ComponentPropsWithRef<typeof DrawerPrimitive.Description>) => (
    <DrawerPrimitive.Description ref={ref} className={cn('text-text-secondary text-sm', className)} {...props} />
);
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

interface DrawerToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
    /**
     * Label text displayed in the toolbar
     */
    label: string;
    /**
     * Whether to show the close button (X icon) on the right side
     * @default true
     */
    showCloseButton?: boolean;
    /**
     * Whether to render the label as a DrawerTitle (heading) instead of a span
     * @default false
     */
    asTitle?: boolean;
}

/**
 * Toolbar section for the drawer, typically used as the first element in DrawerContent.
 * Displays a label with an optional close button.
 *
 * @example
 * ```tsx
 * <DrawerContent showCloseButton={false}>
 *   <DrawerToolbar label="Invoices" />
 *   <DrawerHeader>...</DrawerHeader>
 *   ...
 * </DrawerContent>
 * ```
 */
const DrawerToolbar = ({ label, showCloseButton = true, asTitle = false, className, ...props }: DrawerToolbarProps) => (
    <Toolbar size="default" className={className} {...(props as object)}>
        {/* Toolbar pushes trailing children to the right edge via its expanding title slot.
            DrawerTitle is a plain heading, so it has to take that slot's flex-1 itself or the
            close button ends up beside the label instead of at the right edge. */}
        {asTitle ? <DrawerTitle className="min-w-0 flex-1">{label}</DrawerTitle> : <ToolbarTitle>{label}</ToolbarTitle>}
        {showCloseButton && (
            <DrawerClose asChild>
                <Button variant="ghost" size="icon-sm" aria-label="Close">
                    <XIcon className="size-4" />
                    <span className="sr-only">Close</span>
                </Button>
            </DrawerClose>
        )}
    </Toolbar>
);
DrawerToolbar.displayName = 'DrawerToolbar';

/**
 * Scrollable body section for the drawer, sits between DrawerHeader and DrawerFooter.
 * Automatically handles flex layout and overflow scrolling.
 *
 * @example
 * ```tsx
 * <DrawerContent scrollable>
 *   <DrawerHeader className="p-6">
 *     <DrawerTitle>Title</DrawerTitle>
 *   </DrawerHeader>
 *   <DrawerBody className="gap-4 px-6 py-4">
 *     Content goes here
 *   </DrawerBody>
 *   <DrawerFooter className="px-6 pb-6">
 *     Footer buttons
 *   </DrawerFooter>
 * </DrawerContent>
 * ```
 */
const DrawerBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={cn('flex flex-1 flex-col overflow-y-auto', className)} {...props} />
);
DrawerBody.displayName = 'DrawerBody';

export {
    Drawer,
    DrawerPortal,
    DrawerOverlay,
    DrawerTrigger,
    DrawerClose,
    DrawerContent,
    DrawerHeader,
    DrawerFooter,
    DrawerTitle,
    DrawerDescription,
    DrawerToolbar,
    DrawerBody,
};

// Re-export ToolbarTitle and ActionFooter so consumers can import from one place
export { ToolbarTitle, ActionFooter };
